import React, { useState, useCallback, useMemo } from 'react';
import { StepConfig } from '../focused.view/types';

type StepContext = {
    stepsConfig: Array<StepConfig>;
    currentStepConfig: StepConfig;
    /** 0 - первый шаг анкеты */
    currentStep: number;
    focusedField: string | null;
    areStepsFinished: boolean;
    /** Текст для аналитики, например "Шаг 1" для currentStep = 0 */
    analyticStepText: string;
    setFocusedField(field: string | null): void;
    setShowLanding?(state: boolean): void;
    nextStep(): void;
    prevStep(): void;
    setStep(step: number): void;
};

export const StepContext = React.createContext<StepContext>({} as StepContext);

const getSafeStep = (stepsConfig: Array<StepConfig> = [], unsafeStep: number) => {
    return Math.max(Math.min(unsafeStep, stepsConfig.length - 1), 0);
};

/** Создается один раз, уровнем выше всех шагов-компонентов. Далее - используем контекст */
export const useStepContext = (stepsConfig: Array<StepConfig> = [], defaultStep = 0): StepContext => {
    const [currentStep, setCurrentStep] = useState(defaultStep);
    const [focusedField, setFocusedFieldName] = useState<string | null>(null);
    const currentStepConfig = useMemo(
        () => stepsConfig[getSafeStep(stepsConfig, currentStep)],
        [stepsConfig, currentStep],
    );

    const areStepsFinished = currentStep >= stepsConfig.length;
    const analyticStepText = `Шаг ${currentStep + 1}`;

    const nextStep = useCallback(() => {
        setCurrentStep((prev) => prev + 1);
    }, []);

    const prevStep = useCallback(() => {
        setCurrentStep((prev) => Math.max(prev - 1, 0));
    }, []);

    const setStep = useCallback((step: number) => {
        setCurrentStep(Math.max(step, 0));
    }, []);

    const setFocusedField = useCallback((fieldName: string | null) => {
        setFocusedFieldName(fieldName);
    }, []);

    return useMemo(
        () => ({
            stepsConfig,
            currentStep,
            currentStepConfig,
            nextStep,
            prevStep,
            setStep,
            focusedField,
            setFocusedField,
            areStepsFinished,
            analyticStepText,
        }),
        [currentStep, currentStepConfig, nextStep, prevStep, setStep, focusedField, setFocusedField],
    );
};
